import { CiSearch } from "react-icons/ci";
import { IoMdArrowDropdown } from "react-icons/io";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import {
  clearClientsSearchData,
  setClientsSearchData,
} from "@/store/clientsSearchDataSlice";
import {
  clearOrdersSearchData,
  setOrdersSearchData,
} from "@/store/ordersSearchDataSlice";
import {
  clearCashierOrdersSearchData,
  setCashierOrdersSearchData,
} from "@/store/cashierOrdersSearchDataSlice";
import { setSearchValue, setSelectedSearchOption } from "@/store/searchSlice";
import { useTranslation } from "react-i18next";
import { useUserRole } from "@/hooks/useUserRole";

export default function SearchBarResponsive() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const clientsSearchData = useSelector(
    (state: RootState) => state.clientsSearchData.clientsSearchData,
  );
  const ordersSearchData = useSelector(
    (state: RootState) => state.clientsSearchData.clientsSearchData,
  );
  const cashierOrdersSearchData = useSelector(
    (state: RootState) => state.cashierOrdersSearchData.cashierOrdersSearchData,
  );

  const apps = useSelector((state: RootState) => state.apps.AppsData);
  const cashierApp = apps?.find((app) => app.name === "cashier");
  const dispatch = useDispatch();

  // const [searchValue, setSearchValue] = useState("");
  // const [selectedSearchOption, setSelectedSearchOption] = useState("orders");
  const searchValue = useSelector(
    (state: RootState) => state.search.searchValue,
  );
  const selectedSearchOption = useSelector(
    (state: RootState) => state.search.selectedSearchOption,
  );

  const { isAdmin, isBranchAdmin, isDriver } = useUserRole();

  useEffect(() => {
    if (!clientsSearchData && !ordersSearchData && !cashierOrdersSearchData) {
      dispatch(setSearchValue(""));
    }
  }, [clientsSearchData, ordersSearchData, cashierOrdersSearchData, dispatch]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue === "") {
      return toast.error(t("searchbar.enterValue") as string);
    }

    const toastId = toast.loading(t("searchbar.searching") as string);
    if (selectedSearchOption === "orders") {
      try {
        const response = await axios.get(
          `${baseUrl}/api/dashboard/orders/${searchValue}`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        if (response.data.success == true) {
          toast.success(t("searchbar.resultsFound") as string);
          dispatch(setOrdersSearchData(response.data.order));

          navigate("/orders");
        } else {
          toast.error(t("searchbar.noResults") as string);
        }
      } catch (error) {
        console.error(error);

        toast.error(t("searchbar.error") as string);
      } finally {
        toast.dismiss(toastId);
      }
    } else if (selectedSearchOption === "clients") {
      try {
        const response = await axios.get(
          `${baseUrl}/api/dashboard/clients/search?query=${searchValue}&searchKey=phone`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        if (response.data.success == true && response.data.clients.length > 0) {
          toast.success(t("searchbar.resultsFound") as string);
          dispatch(setClientsSearchData(response.data));
          navigate("/clients");
        } else {
          toast.error(t("searchbar.noResults") as string);
        }
      } catch (error) {
        console.error(error);

        toast.error(t("searchbar.error") as string);
      } finally {
        toast.dismiss(toastId);
      }
    } else if (selectedSearchOption === "cashierOrders") {
      try {
        const response = await axios.get(
          `${baseUrl}/api/dashboard/cashier/orders/${searchValue}`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        if (response.data.success == true) {
          toast.success(t("searchbar.resultsFound") as string);
          navigate("/cashierhistory");
          dispatch(setCashierOrdersSearchData(response.data.order));
        } else {
          toast.error(t("searchbar.noResults") as string);
        }
      } catch (error) {
        console.error(error);

        toast.error(t("searchbar.error") as string);
      } finally {
        toast.dismiss(toastId);
      }
    }
  };

  const baseText = t("searchbar.searchBy");

  const isOrderSearch = selectedSearchOption === "orders";
  const isCashierOrderSearch = selectedSearchOption === "cashierOrders";
  const isClientSearch = selectedSearchOption === "clients"; // Both use "باسم العميل"

  const searchText = isOrderSearch
    ? t("searchbar.orderNumber")
    : isCashierOrderSearch
      ? t("searchbar.cashierOrderNumber")
      : isClientSearch
        ? t("searchbar.clientPhone")
        : t("searchbar.orderNumber"); // Default value

  const placeholderText = `${baseText} ${searchText}`;

  return (
    <>
      <div className="relative mb-10 flex w-full md:hidden">
        <form
          onSubmit={handleSearch}
          className="flex w-full items-center gap-1 rounded-md bg-white ps-1 outline outline-1 -outline-offset-1 outline-gray-300"
        >
          {isClientSearch && lang === "en" && (
            <div className="select-none border-e border-gray-300 px-2 text-base text-gray-500">
              +966
            </div>
          )}

          <input
            dir={
              isClientSearch && lang === "en"
                ? "ltr"
                : lang === "ar" && isClientSearch
                  ? "ltr"
                  : lang === "en"
                    ? "ltr"
                    : "rtl"
            }
            id="search"
            name="search"
            type="tel"
            placeholder={isClientSearch ? "5XXXXXXXX" : placeholderText}
            className="min-w-0 flex-1 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
            value={searchValue}
            onChange={(e) => {
              let value = e.target.value;

              value = value.replace(/\D/g, "");
              if (isClientSearch) {
                // Remove all non-digit characters

                // Remove leading 0s
                if (value.startsWith("0")) {
                  value = value.replace(/^0+/, "");
                }

                // Limit to 9 digits max
                value = value.slice(0, 9);
              }

              dispatch(setSearchValue(value));

              if (value === "") {
                dispatch(clearClientsSearchData());
                dispatch(clearOrdersSearchData());
                dispatch(clearCashierOrdersSearchData());
              }
            }}
          />
          {isClientSearch && lang === "ar" && (
            <div
              dir="ltr"
              className="select-none border-e border-gray-300 px-2 text-base text-gray-500"
            >
              +966
            </div>
          )}
          <div className="relative">
            <select
              value={selectedSearchOption}
              onChange={(e) =>
                dispatch(setSelectedSearchOption(e.target.value))
              }
              id="order"
              name="order"
              className="flex h-full appearance-none items-center justify-center border-s bg-white pe-[12.5px] ps-[2px] text-xs text-gray-500 focus:outline-none"
            >
              <option value="orders" className="">
                {t("searchbar.orders")}
              </option>
              {(isAdmin || isBranchAdmin) && (
                <option value="clients">{t("searchbar.clients")}</option>
              )}
              {cashierApp?.available && cashierApp?.installed && !isDriver && (
                <option value="cashierOrders" className="">
                  {t("searchbar.cashierOrders")}
                </option>
              )}
            </select>
            <IoMdArrowDropdown className="pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 ps-1 text-gray-500" />
          </div>

          <button
            id="searchBtn"
            className="rounded-md bg-sidebar p-2 text-xl text-skyblue transition-opacity hover:opacity-90"
            type="submit"
          >
            <CiSearch strokeWidth={1.1} />
          </button>
        </form>
      </div>
    </>
  );
}
